import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { AvatarUpload, uploadAvatar } from "../components/AvatarUpload";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { useSEO } from "../lib/useSEO";
import { SITE_URL } from "../lib/seo";

const EMERGENT_AUTH_URL = "https://auth.emergentagent.com";

export function AuthPage({ mode }) {
  useSEO({
    title: mode === "register" ? "Sign up | Refine Touch USA" : "Sign in | Refine Touch USA",
    robots: "noindex, follow",
    canonical: `${SITE_URL}/${mode === "register" ? "register" : "login"}`,
  });
  const { login, register, loginGoogle, refresh } = useAuth();
  const nav = useNavigate();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState(params.get("role") || "customer");
  const [busy, setBusy] = useState(false);
  const [pro, setPro] = useState({ avatar: "", location: "", bio: "", services_offered: "", years_experience: "", website: "", instagram: "" });
  const [avatarFile, setAvatarFile] = useState(null);
  const setP = (k) => (e) => setPro({ ...pro, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        const u = await login(email, password);
        toast.success(t("welcome_toast"));
        nav(params.get("next") || (u?.role === "admin" ? "/admin" : u?.role === "provider" ? "/provider" : "/dashboard"));
        return;
      }
      else {
        const extra = role === "provider" ? {
          avatar: pro.avatar || null, location: pro.location || null, bio: pro.bio || null,
          services_offered: pro.services_offered.split(",").map((s) => s.trim()).filter(Boolean),
          years_experience: parseInt(pro.years_experience) || 0,
          website: pro.website || null, instagram: pro.instagram || null,
        } : {};
        await register({ email, password, name, role, ...extra });
        if (role === "provider" && avatarFile) {
          try { await uploadAvatar(avatarFile); await refresh(); } catch (_) { toast.error(t("upload_type_err")); }
        }
      }
      toast.success(t("welcome_toast"));
      nav(params.get("next") || (role === "provider" ? "/provider" : "/dashboard"));
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const startGoogle = () => {
    const redirect = `${window.location.origin}/auth/google-callback`;
    window.location.href = `${EMERGENT_AUTH_URL}/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16 bg-stone-50">
      <div className="w-full max-w-md bg-white border border-stone-200 rounded-3xl p-10 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
        <img src="/brand/refine-touch-logo.jpg" alt="Refine Touch" className="h-14 w-auto object-contain mb-4" />
        <div className="eyebrow text-stone-500 mb-2">Refine Touch</div>
        <h1 className="font-serif text-4xl">{mode === "login" ? t("login_title") : t("signup_title")}</h1>

        <button data-testid="google-btn" onClick={startGoogle} className="w-full mt-6 flex items-center justify-center gap-3 border border-stone-300 rounded-xl h-11 text-sm hover:bg-stone-50">
          <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          {t("login_google")}
        </button>

        <div className="flex items-center gap-3 my-5 text-xs text-stone-400">
          <div className="flex-1 h-px bg-stone-200" />{t("or")}<div className="flex-1 h-px bg-stone-200" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <>
              <div>
                <Label>{t("full_name")}</Label>
                <Input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div>
                <Label>{t("i_am")}</Label>
                <Tabs value={role} onValueChange={setRole} className="mt-2">
                  <TabsList className="grid grid-cols-2 w-full">
                    <TabsTrigger data-testid="role-customer" value="customer">{t("customer")}</TabsTrigger>
                    <TabsTrigger data-testid="role-provider" value="provider">{t("provider")}</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </>
          )}
          <div>
            <Label>{t("email")}</Label>
            <Input data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div>
            <Label>{t("password")}</Label>
            <Input data-testid="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
          </div>
          {mode === "register" && role === "provider" && (
            <div data-testid="provider-extra-fields" className="border-t border-stone-200 pt-5 space-y-3">
              <div>
                <div className="eyebrow text-stone-500 text-xs">{t("ps_section")}</div>
                <p className="text-xs text-stone-500 mt-1">{t("ps_hint")}</p>
              </div>
              <div><Label>{t("ps_location")}</Label><Input data-testid="ps-location" value={pro.location} onChange={setP("location")} placeholder="Miami, FL" required /></div>
              <div><Label>{t("ps_services")}</Label><Input data-testid="ps-services" value={pro.services_offered} onChange={setP("services_offered")} placeholder={t("ps_services_ph")} required /></div>
              <div><Label>{t("ps_bio")}</Label><Textarea data-testid="ps-bio" value={pro.bio} onChange={setP("bio")} rows={3} placeholder={t("ps_bio_ph")} required /></div>
              <AvatarUpload mode="deferred" onFile={setAvatarFile} testId="ps-avatar-upload" />
              <div className="grid grid-cols-3 gap-3">
                <div><Label>{t("ps_experience")}</Label><Input data-testid="ps-exp" type="number" min="0" value={pro.years_experience} onChange={setP("years_experience")} /></div>
                <div><Label>{t("ps_website")}</Label><Input value={pro.website} onChange={setP("website")} /></div>
                <div><Label>{t("ps_instagram")}</Label><Input value={pro.instagram} onChange={setP("instagram")} placeholder="@" /></div>
              </div>
            </div>
          )}
          <Button data-testid="auth-submit" type="submit" disabled={busy} className="w-full h-11 rounded-xl bg-stone-900 hover:bg-stone-800 text-stone-50 btn-pill">
            {busy ? "..." : (mode === "login" ? t("nav_login") : t("nav_signup"))}
          </Button>
        </form>

        <div className="text-center text-sm text-stone-600 mt-6">
          {mode === "login" ? (
            <>{t("no_account")} <Link to="/register" className="underline">{t("nav_signup")}</Link></>
          ) : (
            <>{t("already_account")} <Link to="/login" className="underline">{t("nav_login")}</Link></>
          )}
        </div>
      </div>
    </div>
  );
}

export function GoogleCallbackPage() {
  useSEO({ title: "Signing you in… | Refine Touch USA", robots: "noindex, nofollow" });
  const { loginGoogle } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  React.useEffect(() => {
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sid = params.get("session_id");
    if (!sid) { nav("/login"); return; }
    loginGoogle(sid).then((u) => { toast.success(t("google_connected")); nav(u?.role === "admin" ? "/admin" : u?.role === "provider" ? "/provider" : "/dashboard"); })
      .catch((e) => { toast.error(e.message); nav("/login"); });
  }, []);
  return <div className="min-h-[60vh] flex items-center justify-center">{t("connecting")}</div>;
}
